import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { BRAND, D, fmt, fmtDate, fmtDateTime, fmtSigned, fmtUsd, fmtUsdSigned, openFeeLabel, searchInstruments, type AccountSummary, type Instrument, type InstrumentCategory, type KycStatus, type LeverageOverride, type Position, type Role } from '@platform/shared';
import { InstrumentIcon } from '../components/InstrumentIcon';
import { SectionBoundary } from '../components/SectionBoundary';
import { ClientCell, clientHaystack, clientLabel } from '../components/ClientCell';
import { ClientNavBar, type ClientNav } from '../components/ClientNav';
import { CopyId, cx, Empty, fmtTradeId, Modal, sideLabel, Spinner, TradeId } from '../components/ui';
import { api, ApiError } from '../lib/api';
import { createStore, useStore } from '../lib/store';
import { reloadLogos } from '../stores/logos';
import { useMe } from '../stores/session';
import { FeesTab } from './AdminFees';
import { WithdrawLimitsTab } from './AdminWithdrawLimits';
import { AcademyAdminTab } from './AdminAcademy';
import { DepositsTab, KycTab, RolloutTab, SofTab, SupportTab, WithdrawalsTab } from './AdminAccountTabs';
import { CryptoProTab } from './AdminCryptoPro';
import { FeaturesTab } from './AdminFeatures';
import { FeedsTab } from './AdminFeeds';
import { ProvidersTab } from './AdminProviders';
import { AddInstrumentModal, ArchiveModal } from './AdminAddInstrument';
import { CopyTab } from './AdminCopy';
import { MastersTab } from './AdminMasters';
import { SmartPortfoliosTab } from './AdminPortfolios';
import { MemeTab } from './AdminMeme';
import { EarnSettingsTab, UserEarnPanel, YieldAdminTab } from './AdminEarn';
import { PushTab } from './AdminPush';
import { MoneyCard } from './AdminMoney';
import { VerificationWalletsTab, WalletCards } from './AdminWallets';
import { AgentSendCard } from './AdminAgentMessages';
import { ActionLinksCard } from './AdminActionLinks';
import { KYC_LABEL } from './account/kit';
import { ApprovalsTab, AuditTab, StaffTab } from './AdminDesk';
import { DeskAccountsTab } from './AdminDeskAccounts';
import { DeskNotices } from './AdminDeskDealing';
import { OnlineTab, useTeamHeartbeat } from './AdminDeskOnline';
import { DailySummaryTab, RiskDeskTab } from './AdminDeskRisk';
import { AdminHome, DealerHome } from './AdminHome';
import { MySignInSecurityTab } from './AdminSignIn';
import { GuideTab } from './AdminGuide';
import { AdminNav, ALL_TABS, DEALER_TABS, itemOf, navFor, TAB_LABEL, type Tab } from './AdminNav';
import { paintChrome } from '../lib/theme';

/** BACKOFFICE_IN_CRM: user status, credit / debit, KYC, withdrawals, deposits and support are handled in the CRM. */
const adminConfig = createStore({ backofficeInCrm: false });
const useBackofficeInCrm = () => useStore(adminConfig, (s) => s.backofficeInCrm);

// ---------- shared ----------

function useLoad<T>(path: string | null, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const reload = useCallback(async () => {
    if (!path) return;
    setLoading(true);
    try {
      setData(await api.get<T>(path));
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [path]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    void reload();
  }, [reload, ...deps]); // eslint-disable-line react-hooks/exhaustive-deps
  return { data, error, loading, reload };
}

const th = 'px-3 py-2 text-left text-[11px] font-medium text-muted whitespace-nowrap';
const td = 'px-3 py-2 whitespace-nowrap';
const input = 'h-9 rounded-lg border border-line bg-bg px-3 text-[13px] text-fg outline-none focus:border-line-strong placeholder:text-muted';
const btn = 'h-8 rounded-md border border-line-strong px-3 text-xs font-semibold text-fg disabled:opacity-50';

function Table({ head, children, minWidth = 900 }: { head: string[]; children: ReactNode; minWidth?: number }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-line bg-panel">
      <table className="w-full border-collapse text-xs" style={{ minWidth }}>
        <thead className="border-b border-line">
          <tr>
            {head.map((h) => (
              <th key={h} className={th}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

function Err({ msg }: { msg: string | null }) {
  return msg ? (
    <p className="m-0 text-xs text-down" role="alert">
      {msg}
    </p>
  ) : null;
}

/** Hedge level fraction ("0.25") ↔ percent ("25"). */
const toPct = (level: string) => D.from(level).mul(100).toString();
function fromPct(pct: string): string | null {
  const v = pct.trim();
  if (!/^\d{1,3}(\.\d{1,2})?$/.test(v) || D.from(v).gt(100)) return null;
  return D.from(v).div(100).toString();
}

/** A hedge level as a percent input + Save; `onSave` gets the fraction. */
function HedgeLevelInput({ label, value, onSave }: { label: string; value: string; onSave: (level: string) => Promise<void> }) {
  const [v, setV] = useState(toPct(value));
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => setV(toPct(value)), [value]);
  const save = async (e: FormEvent) => {
    e.preventDefault();
    const level = fromPct(v);
    if (level === null) return setErr('Enter a percentage from 0 to 100');
    setErr(null);
    setBusy(true);
    try {
      await onSave(level);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={save} className="flex flex-col gap-1">
      <div className="flex items-end gap-2">
        <label className="flex flex-col gap-1 text-[11px] text-muted">
          {label}
          <span className="flex items-center gap-1.5">
            <input value={v} onChange={(e) => setV(e.target.value)} inputMode="decimal" className={cx(input, 'num w-20')} />
            <span className="text-xs text-fg">%</span>
          </span>
        </label>
        <button type="submit" disabled={busy || v === toPct(value)} className="h-9 rounded-md bg-accent px-4 text-xs font-semibold text-white disabled:opacity-50">
          {busy ? <Spinner /> : 'Save'}
        </button>
      </div>
      <Err msg={err} />
    </form>
  );
}

const pnlCls = (v: string | null | undefined) => (!v ? 'text-muted' : D.from(v).isNeg() ? 'text-down' : D.from(v).isPos() ? 'text-up' : '');

// ---------- Users ----------

interface AdminUserRow {
  id: string;
  email: string;
  /** Profile name, else the CRM name. */
  name: string | null;
  role: string;
  /** A retention agent's test account, opened and deleted by the CRM. */
  testAccount: boolean;
  status: string;
  externalClientId: string | null;
  createdAt: string;
  liveBalance: string | null;
  demoBalance: string | null;
  openPositions: number;
}
interface AdminUser {
  id: string;
  email: string;
  name: string | null;
  role: string;
  status: string;
  kycStatus: KycStatus;
  externalClientId: string | null;
  createdAt: string;
  accounts: AccountSummary[];
  positions: Position[];
  /** Share of this client's exposure to hedge (0–1); null = the all-clients level. */
  hedgeLevel: string | null;
  defaultHedgeLevel: string;
  /** The client's latest demo account request (any status), or null. */
  demoRequest: { status: 'pending' | 'approved' | 'declined' | 'cancelled'; created_at: string; reason: string | null; note: string | null; decided_by: string | null } | null;
}

const DECLINE_REASONS: [string, string][] = [
  ['start_live', 'Should start on live'],
  ['not_suitable', 'Not suitable'],
  ['duplicate', 'Duplicate request'],
  ['other', 'Other'],
];

/** Verified / not verified set by hand. Decides a pending submission too; the client is told either way. */
function KycControl({ userId, status, inCrm, onDone }: { userId: string; status: KycStatus; inCrm: boolean; onDone: () => void }) {
  const [unverifying, setUnverifying] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const k = KYC_LABEL[status] ?? KYC_LABEL.none;
  const tone = { up: 'bg-up-bg text-up', down: 'bg-down-bg text-down', warn: 'bg-warn-bg text-warn', accent: 'bg-accent-bg text-accent-fg' }[k.tone];
  const set = async (verified: boolean) => {
    if (verified && !window.confirm('Mark this client as verified? Any pending KYC submission is approved, and the client is told by email and notification.')) return;
    setBusy(true);
    setMsg(null);
    try {
      const r = await api.post<{ status: KycStatus }>(`/api/admin/users/${userId}/kyc`, { verified, reason });
      setMsg({ ok: true, text: `KYC is now: ${(KYC_LABEL[r.status] ?? KYC_LABEL.none).label}` });
      setUnverifying(false);
      setReason('');
      onDone();
    } catch (e) {
      setMsg({ ok: false, text: e instanceof ApiError ? e.message : 'Failed' });
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-col gap-2.5 rounded-lg border border-line p-3">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="m-0 text-sm font-semibold">KYC</h3>
        <span className={cx('rounded px-2 py-1 text-xs', tone)}>{k.label}</span>
        <div className="flex-1" />
        {!inCrm && status !== 'approved' && (
          <button type="button" disabled={busy} className={cx(btn, 'border-up text-up')} onClick={() => void set(true)}>
            Mark verified
          </button>
        )}
        {!inCrm && status !== 'none' && status !== 'rejected' && !unverifying && (
          <button type="button" disabled={busy} className={cx(btn, 'border-down text-down')} onClick={() => setUnverifying(true)}>
            Mark not verified
          </button>
        )}
      </div>
      {inCrm && <p className="m-0 text-xs text-muted">KYC for this client is managed in the CRM.</p>}
      {unverifying && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void set(false);
          }}
          className="flex flex-wrap gap-2"
        >
          <input required autoFocus placeholder="Reason (sent to the client)" value={reason} onChange={(e) => setReason(e.target.value)} className={cx(input, 'min-w-0 flex-1')} aria-label="Reason for not verified" />
          <button type="submit" disabled={busy || !reason.trim()} className="h-9 rounded-lg bg-btn-short px-4 text-xs font-semibold text-white disabled:opacity-50">
            {busy ? <Spinner /> : 'Mark not verified'}
          </button>
          <button type="button" className={cx(btn, 'h-9')} onClick={() => setUnverifying(false)}>
            Cancel
          </button>
        </form>
      )}
      {msg && <span className={cx('text-xs', msg.ok ? 'text-up' : 'text-down')}>{msg.text}</span>}
    </div>
  );
}

function UserDetail({ id, onClose, onChanged, nav }: { id: string; onClose: () => void; onChanged: () => void; nav?: ClientNav }) {
  const { data: u, reload, error } = useLoad<AdminUser>(`/api/admin/users/${id}`);
  const inCrm = useBackofficeInCrm();
  const [ext, setExt] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const patch = async (body: object) => {
    setMsg(null);
    try {
      await api.patch(`/api/admin/users/${id}`, body);
      await reload();
      onChanged();
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message : 'Failed');
    }
  };
  const remove = async () => {
    if (!u || !window.confirm(`Delete ${u.email}? Open positions and orders are closed, and all personal data, KYC, files, tickets and sessions are erased. This cannot be undone.`)) return;
    setMsg(null);
    try {
      await api.del(`/api/admin/users/${id}`);
      onChanged();
      onClose();
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message : 'Delete failed');
    }
  };
  const openDemo = async () => {
    setMsg(null);
    try {
      await api.post(`/api/admin/users/${id}/demo`);
      await reload();
      onChanged();
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message : 'Failed');
    }
  };
  const [declineReason, setDeclineReason] = useState('start_live');
  const declineDemo = async () => {
    setMsg(null);
    try {
      await api.post(`/api/admin/users/${id}/demo-request/decline`, { reason: declineReason });
      await reload();
      onChanged();
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message : 'Failed');
    }
  };
  const close = async (pid: string) => {
    setMsg(null);
    try {
      await api.post(`/api/admin/positions/${pid}/close`);
      await reload();
      onChanged();
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message : 'Close failed');
    }
  };
  return (
    <Modal open onClose={onClose} title={u ? clientLabel({ name: u.name, email: u.email, crmId: u.externalClientId }) : 'User'} width={760}>
      {nav && nav.total > 1 && <ClientNavBar nav={nav} />}
      <Err msg={error ?? msg} />
      {!u ? (
        <Spinner />
      ) : (
        <div className="flex max-h-[70vh] flex-col gap-4 overflow-y-auto">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="rounded bg-raised px-2 py-1">{u.role}</span>
            <span className={cx('rounded px-2 py-1', u.status === 'active' ? 'bg-up-bg text-up' : 'bg-down-bg text-down')}>{u.status}</span>
            <span>{u.email}</span>
            <span className="text-muted">Joined {fmtDate(u.createdAt)}</span>
            <CopyId value={u.id} label="user ID" className="num font-mono text-muted" />
            <div className="flex-1" />
            {!inCrm && (
              <button type="button" className={btn} onClick={() => void patch({ status: u.status === 'active' ? 'disabled' : 'active' })}>
                {u.status === 'active' ? 'Disable user' : 'Enable user'}
              </button>
            )}
            <button type="button" className={cx(btn, 'border-down text-down')} onClick={() => void remove()}>
              Delete user
            </button>
          </div>
          <div className="flex items-center gap-2">
            <input placeholder="External client ID (CRM)" value={ext ?? u.externalClientId ?? ''} onChange={(e) => setExt(e.target.value)} className={cx(input, 'flex-1')} aria-label="External client ID" />
            <button type="button" className={btn} disabled={ext === null} onClick={() => void patch({ externalClientId: ext || null }).then(() => setExt(null))}>
              Save
            </button>
          </div>
          {u.role === 'user' && (
            <KycControl
              userId={u.id}
              status={u.kycStatus}
              inCrm={inCrm}
              onDone={() => {
                void reload();
                onChanged();
              }}
            />
          )}
          {u.role === 'user' && <AgentSendCard userId={u.id} />}
          {u.role === 'user' && <ActionLinksCard userId={u.id} />}
          {u.role === 'user' && <UserEarnPanel userId={u.id} />}
          <div className="flex flex-wrap items-end gap-3 rounded-lg border border-line p-3">
            <HedgeLevelInput
              label="Hedge level"
              value={u.hedgeLevel ?? u.defaultHedgeLevel}
              onSave={async (level) => {
                await api.put(`/api/admin/users/${id}/hedge-level`, { level });
                await reload();
              }}
            />
            {u.hedgeLevel === null ? (
              <span className="pb-2.5 text-xs text-muted">Uses the all-clients level ({toPct(u.defaultHedgeLevel)}%)</span>
            ) : (
              <button
                type="button"
                className={cx(btn, 'h-9')}
                onClick={() =>
                  void api
                    .put(`/api/admin/users/${id}/hedge-level`, { level: null })
                    .then(reload)
                    .catch((e) => setMsg(e instanceof ApiError ? e.message : 'Failed'))
                }
              >
                Use all-clients level ({toPct(u.defaultHedgeLevel)}%)
              </button>
            )}
            <p className="m-0 w-full text-[11px] text-muted">Share of this client&apos;s live exposure the house hedges at the liquidity provider. Staff only: the client never sees it, and it does not change their prices or margin.</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {u.accounts.map((a) => (
              <div key={a.id} className="rounded-lg border border-line p-3 text-xs">
                <div className="mb-1 font-semibold"><span className="capitalize">{a.kind}</span> <span className="font-normal text-muted">USDT</span></div>
                <div className="num grid grid-cols-2 gap-1">
                  <span className="text-muted">Balance</span>
                  <span className={pnlCls(D.from(a.balance).isNeg() ? a.balance : '0')}>{fmtUsd(a.balance)}</span>
                  <span className="text-muted">Credit</span>
                  <span>{fmtUsd(a.credit)}</span>
                  <span className="text-muted">Withdrawable</span>
                  <span>{fmtUsd(a.withdrawable)}</span>
                  <span className="text-muted">Available</span>
                  <span>{fmtUsd(a.available)}</span>
                  <span className="text-muted">Margin used</span>
                  <span>{fmtUsd(a.marginUsed)}</span>
                  <span className="text-muted">Unrealized</span>
                  <span className={pnlCls(a.unrealizedPnl)}>{fmtUsdSigned(a.unrealizedPnl)}</span>
                </div>
              </div>
            ))}
            {/* Clients ask for a demo account from the web app; opening one here approves the request. */}
            {!u.accounts.some((a) => a.kind === 'demo') &&
              (u.demoRequest?.status === 'pending' ? (
                <div className="flex flex-col items-start justify-center gap-2 rounded-lg border border-warn bg-warn-bg p-3 text-xs text-warn">
                  <span className="font-semibold">Demo account requested · {fmtDateTime(u.demoRequest.created_at)}</span>
                  <span className="text-muted">Notice accepted (not real money, education only)</span>
                  <div className="flex flex-wrap items-center gap-2">
                    <button type="button" className={btn} onClick={() => void openDemo()}>
                      Approve
                    </button>
                    <select value={declineReason} onChange={(e) => setDeclineReason(e.target.value)} className={input} aria-label="Decline reason">
                      {DECLINE_REASONS.map(([k, l]) => (
                        <option key={k} value={k}>
                          {l}
                        </option>
                      ))}
                    </select>
                    <button type="button" className={btn} onClick={() => void declineDemo()}>
                      Decline
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-start justify-center gap-2 rounded-lg border border-dashed border-line p-3 text-xs text-muted">
                  {u.demoRequest?.status === 'declined' ? `Demo request declined by ${u.demoRequest.decided_by ?? 'staff'}` : 'No demo account'}
                  <button type="button" className={btn} onClick={() => void openDemo()}>
                    Open demo account
                  </button>
                </div>
              ))}
          </div>
          {inCrm ? (
            <p className="m-0 rounded-lg border border-line p-3 text-xs text-muted">Status, deposits, withdrawals and credit for this client are managed in the CRM.</p>
          ) : (
            <MoneyCard accounts={u.accounts} email={u.email} onDone={() => void reload().then(onChanged)} />
          )}
          <div>
            <h3 className="m-0 mb-2 text-sm font-semibold">Withdrawal wallets</h3>
            <WalletCards userId={u.id} readOnly={inCrm} />
          </div>
          <div>
            <h3 className="m-0 mb-2 text-sm font-semibold">Open positions</h3>
            {u.positions.length ? (
              <Table head={['Order ID', 'Account', 'Symbol', 'Side', 'Qty', 'Entry', 'Margin ($)', 'Liq', '']} minWidth={680}>
                {u.positions.map((p) => (
                  <tr key={p.id} className="num border-b border-line">
                    <td className={td}>
                      <TradeId id={p.tradeId} />
                    </td>
                    <td className={td}>{u.accounts.find((a) => a.id === p.accountId)?.kind}</td>
                    <td className={cx(td, 'font-sans font-semibold')}>
              <span className="flex items-center gap-2">
                <InstrumentIcon symbol={p.symbol} size={18} />
                {p.symbol}
              </span>
            </td>
                    <td className={cx(td, p.side === 'long' ? 'text-up' : 'text-down')}>
                      {sideLabel(p.side)} {p.leverage}x
                    </td>
                    <td className={td}>{p.qty}</td>
                    <td className={td}>{fmt(p.entryPrice, 4)}</td>
                    <td className={td}>{fmt((p.marginHeld ?? p.margin), 2)}</td>
                    <td className={cx(td, 'text-warn')}>{p.liqPrice ? fmt(p.liqPrice, 4) : p.hedged ? 'Hedged' : '—'}</td>
                    <td className={td}>
                      <button type="button" className={btn} onClick={() => void close(p.id)}>
                        Close
                      </button>
                    </td>
                  </tr>
                ))}
              </Table>
            ) : (
              <p className="m-0 text-xs text-muted">No open positions</p>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

function NewUserForm({ onDone }: { onDone: () => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<Role>('user');
  const [demo, setDemo] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setMsg(null);
    try {
      await api.post('/api/admin/users', { email, password, role, demo });
      onDone();
    } catch (err) {
      setMsg(err instanceof ApiError ? err.message : 'Failed');
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={(e) => void submit(e)} className="flex flex-wrap items-center gap-2 rounded-xl border border-line bg-panel p-3">
      <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className={cx(input, 'min-w-[200px] flex-1')} aria-label="New user email" />
      <input type="text" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password (8+ chars, letter and digit)" className={cx(input, 'min-w-[200px] flex-1')} aria-label="New user password" autoComplete="new-password" />
      <select value={role} onChange={(e) => setRole(e.target.value as Role)} className={input} aria-label="Role">
        <option value="user">user</option>
        <option value="admin">admin</option>
        <option value="dealer">dealer (dealing only)</option>
      </select>
      <label className="flex items-center gap-1.5 text-xs text-muted">
        <input type="checkbox" checked={demo} onChange={(e) => setDemo(e.target.checked)} />
        Also open a demo account
      </label>
      <button type="submit" className={btn} disabled={busy}>
        Create
      </button>
      <Err msg={msg} />
    </form>
  );
}

function UsersTab() {
  const [query, setQuery] = useState('');
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const { data, error, loading, reload } = useLoad<AdminUserRow[]>(`/api/admin/users?q=${encodeURIComponent(term)}&limit=200`);
  const openIndex = data && open ? data.findIndex((u) => u.id === open) : -1;
  return (
    <div className="flex flex-col gap-3">
      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setTerm(query);
        }}
      >
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, email, CRM ID or user ID" className={cx(input, 'flex-1')} aria-label="Search users" />
        <button type="submit" className={btn}>
          Search
        </button>
        <button type="button" className={btn} onClick={() => setAdding((a) => !a)}>
          {adding ? 'Cancel' : 'Add user'}
        </button>
      </form>
      {adding && (
        <NewUserForm
          onDone={() => {
            setAdding(false);
            void reload();
          }}
        />
      )}
      <Err msg={error} />
      <Table head={['Client', 'Role', 'Status', 'Live balance ($)', 'Demo balance ($)', 'Open pos.', 'Joined']}>
        {data?.map((u) => (
          <tr key={u.id} onClick={() => setOpen(u.id)} className="cursor-pointer border-b border-line hover:bg-raised">
            <td className={td}>
              <ClientCell name={u.name} email={u.email} crmId={u.externalClientId} onOpen={() => setOpen(u.id)} />
            </td>
            <td className={td}>
              {u.role}
              {u.testAccount && <span className="ml-1.5 rounded bg-warn/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-warn">Test</span>}
            </td>
            <td className={cx(td, u.status === 'active' ? 'text-up' : 'text-down')}>{u.status}</td>
            <td className={cx(td, 'num')}>{fmt(u.liveBalance, 2)}</td>
            <td className={cx(td, 'num text-muted')}>{fmt(u.demoBalance, 2)}</td>
            <td className={cx(td, 'num')}>{u.openPositions}</td>
            <td className={cx(td, 'text-muted')}>{fmtDate(u.createdAt)}</td>
          </tr>
        ))}
      </Table>
      {loading && !data && <Spinner />}
      {data && !data.length && <Empty>No users found</Empty>}
      {open && (
        <UserDetail
          key={open}
          id={open}
          onClose={() => setOpen(null)}
          onChanged={() => void reload()}
          nav={data && openIndex >= 0 ? { index: openIndex, total: data.length, go: (i) => setOpen(data[i]?.id ?? open) } : undefined}
        />
      )}
    </div>
  );
}

// ---------- Positions ----------

type AdminPosition = Position & { email: string; name: string | null; externalClientId: string | null; accountKind: string; unrealizedPnl: string | null; mark: string | null };

/** Positions search: a trade ID ("100245" or "#100245"), or the client's name, email or CRM ID, or the symbol. */
function matchesPosition(p: AdminPosition, term: string) {
  const t = term.trim().toLowerCase();
  if (!t) return true;
  const id = /^#?(\d+)$/.exec(t)?.[1];
  if (id && String(p.tradeId ?? '') === id) return true;
  return clientHaystack({ name: p.name, email: p.email, crmId: p.externalClientId }).includes(t.replace(/^#/, '')) || p.symbol.toLowerCase().includes(t);
}

function KindToggle({ value, onChange, all }: { value: string; onChange: (v: string) => void; all?: boolean }) {
  const opts = all ? ['live', 'demo', 'all'] : ['live', 'demo'];
  return (
    <div role="group" className="flex rounded-full border border-line bg-panel p-[3px]">
      {opts.map((k) => (
        <button key={k} type="button" aria-pressed={value === k} onClick={() => onChange(k)} className={cx('h-[26px] rounded-full px-3 text-xs font-semibold capitalize', value === k ? 'bg-raised text-fg' : 'text-muted')}>
          {k}
        </button>
      ))}
    </div>
  );
}

function PositionsTab() {
  const [kind, setKind] = useState('live');
  const { data, error, reload } = useLoad<AdminPosition[]>(`/api/admin/positions?kind=${kind}`);
  const [msg, setMsg] = useState<string | null>(null);
  const [term, setTerm] = useState('');
  useEffect(() => {
    const t = window.setInterval(() => void reload(), 5000);
    return () => window.clearInterval(t);
  }, [reload]);
  const rows = data?.filter((p) => matchesPosition(p, term));
  const close = async (p: AdminPosition) => {
    const who = clientLabel({ name: p.name, email: p.email, crmId: p.externalClientId });
    if (!window.confirm(`Close ${p.tradeId != null ? `order ${fmtTradeId(p.tradeId)}` : 'this position'} (${who} · ${p.symbol}) at the current price?`)) return;
    try {
      await api.post(`/api/admin/positions/${p.id}/close`);
      await reload();
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message : 'Close failed');
    }
  };
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <KindToggle value={kind} onChange={setKind} all />
        <input type="search" value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Search trade ID, client, CRM ID or symbol" className={cx(input, 'w-72')} aria-label="Search positions" />
        <span className="text-xs text-muted">
          {term.trim() && rows ? `${rows.length} of ` : ''}
          {data?.length ?? 0} open positions · refreshes every 5s
        </span>
      </div>
      <Err msg={error ?? msg} />
      <Table head={['Order ID', 'Client', 'Acct', 'Symbol', 'Side', 'Qty', 'Entry', 'Mark', 'Margin ($)', 'Unrealized ($)', 'Liq', 'TP / SL', 'Opened', '']} minWidth={1180}>
        {rows?.map((p) => (
          <tr key={p.id} className="num border-b border-line">
            <td className={td}>
              <TradeId id={p.tradeId} />
            </td>
            <td className={td}>
              <ClientCell name={p.name} email={p.email} crmId={p.externalClientId} />
            </td>
            <td className={cx(td, 'font-sans text-muted')}>{p.accountKind}</td>
            <td className={cx(td, 'font-sans font-semibold')}>
              <span className="flex items-center gap-2">
                <InstrumentIcon symbol={p.symbol} size={18} />
                {p.symbol}
              </span>
            </td>
            <td className={cx(td, p.side === 'long' ? 'text-up' : 'text-down')}>
              {sideLabel(p.side)} {p.leverage}x
            </td>
            <td className={td}>{p.qty}</td>
            <td className={td}>{fmt(p.entryPrice, 4)}</td>
            <td className={td}>{p.mark ? fmt(p.mark, 4) : '—'}</td>
            <td className={td}>{fmt((p.marginHeld ?? p.margin), 2)}</td>
            <td className={cx(td, pnlCls(p.unrealizedPnl))}>{p.unrealizedPnl ? fmtSigned(p.unrealizedPnl, 2) : '—'}</td>
            <td className={cx(td, 'text-warn')}>{p.liqPrice ? fmt(p.liqPrice, 4) : p.hedged ? 'Hedged' : '—'}</td>
            <td className={td}>
              {p.tpPrice ? fmt(p.tpPrice, 4) : '—'} / {p.slPrice ? fmt(p.slPrice, 4) : '—'}
            </td>
            <td className={cx(td, 'text-muted')}>{fmtDateTime(p.openedAt)}</td>
            <td className={td}>
              <button type="button" className={btn} onClick={() => void close(p)}>
                Close
              </button>
            </td>
          </tr>
        ))}
      </Table>
      {data && !data.length && <Empty>No open positions</Empty>}
      {data && !!data.length && rows && !rows.length && <Empty>No positions match.</Empty>}
    </div>
  );
}

// ---------- Instruments ----------

type AdminInstrument = Instrument & { leverageOverride: LeverageOverride; archivedAt: string | null; providerSymbols: Record<string, string> };

const LEV_FIELDS = [
  { key: 'min', prop: 'minLeverage', label: 'Min leverage' },
  { key: 'default', prop: 'defaultLeverage', label: 'Default leverage' },
  { key: 'max', prop: 'maxLeverage', label: 'Max leverage' },
] as const;

/** 'optdec': a decimal that may be blank (saved as null). */
const FIELDS: { key: keyof Instrument; label: string; kind: 'dec' | 'optdec' | 'int' | 'signed' | 'text' }[] = [
  { key: 'hedgedMarginRate', label: 'Hedged margin rate (0–1, 0 = free)', kind: 'dec' },
  { key: 'spreadBps', label: 'Min spread (bps)', kind: 'dec' },
  { key: 'markupBps', label: 'Markup per side (bps)', kind: 'dec' },
  { key: 'spreadPips', label: 'Fixed spread (pips; blank = bps above)', kind: 'optdec' },
  { key: 'feeRate', label: 'Fee rate (0.0005 = 0.05%)', kind: 'dec' },
  { key: 'feeFlat', label: 'Flat fee per trade ($, on open)', kind: 'dec' },
  { key: 'swapLongRate', label: 'Swap buy (% / day)', kind: 'signed' },
  { key: 'swapShortRate', label: 'Swap sell (% / day)', kind: 'signed' },
  { key: 'minNotional', label: 'Min order ($)', kind: 'dec' },
  { key: 'maxNotional', label: 'Max order ($)', kind: 'dec' },
  { key: 'lotSize', label: 'Lot size (units per lot)', kind: 'dec' },
  { key: 'unit', label: 'Unit label (oz, share, BTC …)', kind: 'text' },
  { key: 'minLots', label: 'Min volume (lots)', kind: 'dec' },
  { key: 'lotStep', label: 'Volume step (lots)', kind: 'dec' },
  { key: 'depthLevels', label: 'Book levels', kind: 'int' },
  { key: 'depthStepBps', label: 'Book step (bps)', kind: 'dec' },
];

const hasOverride = (i: AdminInstrument) => LEV_FIELDS.some((f) => i.leverageOverride[f.key] !== null);

/** Instrument fields a dealer sees but can't change (the API refuses them too). */
const ADMIN_ONLY_FIELDS: string[] = ['feeRate', 'feeFlat', 'swapLongRate', 'swapShortRate'];

/** Read a picked file as a data: URL. */
const readDataUrl = (f: File) =>
  new Promise<string>((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(String(r.result));
    r.onerror = () => rej(new Error('Could not read the file'));
    r.readAsDataURL(f);
  });

/** The instrument's logo: from the data provider, uploaded here, or none (then a letter mark is drawn). Admins can upload or remove one. */
function LogoField({ inst }: { inst: AdminInstrument }) {
  const dealer = useMe()?.role === 'dealer';
  const { data, reload } = useLoad<{ source: 'upload' | 'provider' | null }>(dealer ? null : `/api/admin/instruments/${inst.id}/logo`);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<unknown>) => {
    setErr(null);
    setBusy(true);
    try {
      await fn();
      await reload();
      reloadLogos();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const pick = (f: File | undefined) => {
    if (!f) return;
    if (f.size > 256 * 1024) return setErr('The logo must be 256 KB or smaller');
    void run(async () => api.put(`/api/admin/instruments/${inst.id}/logo`, { image: await readDataUrl(f) }));
  };
  const source = data?.source;
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-xl bg-raised/60 p-3">
      <InstrumentIcon symbol={inst.symbol} size={40} />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5 text-xs">
        <span className="font-semibold text-fg">Logo</span>
        <span className="text-muted">
          {dealer
            ? 'Shown wherever the instrument appears.'
            : source === 'upload'
              ? 'Uploaded here. Shown everywhere the instrument appears.'
              : source === 'provider'
                ? 'From the data provider. Upload one to replace it.'
                : 'No logo saved: the icon above is drawn by the app. Upload one so clients always see this logo.'}
        </span>
        <Err msg={err} />
      </div>
      {!dealer && (
        <div className="flex gap-2">
          <label className={cx(btn, 'inline-flex cursor-pointer items-center', busy && 'pointer-events-none opacity-50')}>
            {busy ? <Spinner /> : source ? 'Replace logo' : 'Upload logo'}
            <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="sr-only" onChange={(e) => (pick(e.target.files?.[0]), (e.target.value = ''))} />
          </label>
          {source === 'upload' && (
            <button type="button" disabled={busy} onClick={() => run(() => api.del(`/api/admin/instruments/${inst.id}/logo`))} className={btn}>
              Remove
            </button>
          )}
        </div>
      )}
      {!dealer && <p className="m-0 w-full text-[11px] text-muted">PNG, JPEG, WebP or GIF, square, up to 256 KB.</p>}
    </div>
  );
}

function InstrumentEditor({ inst, categories, onClose, onSaved }: { inst: AdminInstrument; categories: InstrumentCategory[]; onClose: () => void; onSaved: () => void }) {
  const [vals, setVals] = useState<Record<string, string>>(() => Object.fromEntries(FIELDS.map((f) => [f.key, String(inst[f.key] ?? '')])));
  const [hours, setHours] = useState(JSON.stringify(inst.tradingHours));
  const [categoryId, setCategoryId] = useState(inst.categoryId);
  // Leverage overrides: blank = use the category's value.
  const [lev, setLev] = useState<Record<string, string>>(() => Object.fromEntries(LEV_FIELDS.map((f) => [f.key, inst.leverageOverride[f.key]?.toString() ?? ''])));
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const cat = categories.find((c) => c.id === categoryId);
  const dealer = useMe()?.role === 'dealer';
  const save = async (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    const body: Record<string, unknown> = {};
    for (const f of FIELDS) {
      const v = vals[f.key]!.trim();
      if (v === String(inst[f.key] ?? '')) continue;
      body[f.key] = f.kind === 'int' ? Number(v) : f.kind === 'optdec' && v === '' ? null : v;
    }
    if (categoryId !== inst.categoryId) body.categoryId = categoryId;
    for (const f of LEV_FIELDS) {
      const v = lev[f.key]!.trim();
      const next = v === '' ? null : Number(v);
      if (next !== null && !Number.isInteger(next)) return setErr(`${f.label} must be a whole number, or blank to use the category's`);
      if (next !== inst.leverageOverride[f.key]) body[f.prop] = next;
    }
    if (hours !== JSON.stringify(inst.tradingHours)) {
      try {
        body.tradingHours = JSON.parse(hours);
      } catch {
        return setErr('Trading hours must be valid JSON');
      }
    }
    if (!Object.keys(body).length) return onClose();
    setBusy(true);
    try {
      await api.patch(`/api/admin/instruments/${inst.id}`, body);
      onSaved();
      onClose();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal open onClose={onClose} title={`Edit ${inst.symbol}`} width={620}>
      <form onSubmit={save} className="flex flex-col gap-3">
        <LogoField inst={inst} />
        <div className="grid grid-cols-2 gap-3">
          <label className="col-span-2 flex flex-col gap-1 text-[11px] text-muted">
            Category
            <select value={categoryId} onChange={(e) => setCategoryId(Number(e.target.value))} className={input}>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.minLeverage}x / {c.defaultLeverage}x / {c.maxLeverage}x)
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {LEV_FIELDS.map((f) => (
            <label key={f.key} className="flex flex-col gap-1 text-[11px] text-muted">
              {f.label}
              <input
                value={lev[f.key]}
                onChange={(e) => setLev((v) => ({ ...v, [f.key]: e.target.value }))}
                placeholder={cat ? `${cat[f.prop]}x from ${cat.name}` : ''}
                inputMode="numeric"
                className={cx(input, 'num')}
              />
            </label>
          ))}
        </div>
        <p className="m-0 text-[11px] text-muted">Leave a leverage field blank to use the category&apos;s value. Open positions and placed orders keep their leverage.</p>
        <div className="grid grid-cols-2 gap-3">
          {FIELDS.map((f) => (
            <label key={f.key} className="flex flex-col gap-1 text-[11px] text-muted">
              {f.label}
              <input
                value={vals[f.key]}
                onChange={(e) => setVals((v) => ({ ...v, [f.key]: e.target.value }))}
                disabled={dealer && ADMIN_ONLY_FIELDS.includes(f.key)}
                title={dealer && ADMIN_ONLY_FIELDS.includes(f.key) ? 'Only an admin can change this' : undefined}
                className={cx(input, 'num disabled:opacity-50')}
              />
            </label>
          ))}
        </div>
        <label className="flex flex-col gap-1 text-[11px] text-muted">
          Trading hours (JSON)
          <textarea value={hours} onChange={(e) => setHours(e.target.value)} rows={3} className="num rounded-lg border border-line bg-bg p-2 text-xs text-fg outline-none focus:border-line-strong" />
        </label>
        <Err msg={err} />
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className={btn}>
            Cancel
          </button>
          <button type="submit" disabled={busy} className="h-8 rounded-md bg-accent px-4 text-xs font-semibold text-white disabled:opacity-50">
            {busy ? <Spinner /> : 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/** Create a category (no `cat`) or edit one: name and min / default / max leverage. */
function CategoryForm({ cat, overrides, onSaved, onDeleted }: { cat?: InstrumentCategory; overrides?: number; onSaved: (c: InstrumentCategory) => void; onDeleted?: () => void }) {
  const init = () => ({ name: cat?.name ?? '', min: String(cat?.minLeverage ?? 1), def: String(cat?.defaultLeverage ?? 10), max: String(cat?.maxLeverage ?? 20) });
  const [v, setV] = useState(init);
  const [err, setErr] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setV(init());
    setErr(null);
    setNote(null);
  }, [cat?.id, cat?.name, cat?.minLeverage, cat?.defaultLeverage, cat?.maxLeverage]); // eslint-disable-line react-hooks/exhaustive-deps
  const run = async (fn: () => Promise<void>) => {
    setErr(null);
    setNote(null);
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Failed');
    } finally {
      setBusy(false);
    }
  };
  const save = (e: FormEvent) => {
    e.preventDefault();
    const body = { name: v.name.trim(), minLeverage: Number(v.min), defaultLeverage: Number(v.def), maxLeverage: Number(v.max) };
    void run(async () => {
      const c = cat ? await api.patch<InstrumentCategory>(`/api/admin/instrument-categories/${cat.id}`, body) : await api.post<InstrumentCategory>('/api/admin/instrument-categories', body);
      if (cat) setNote('Saved. Instruments without their own leverage now use this range.');
      onSaved(c);
    });
  };
  const clear = () =>
    cat &&
    window.confirm(`Remove the leverage overrides of ${overrides} instrument${overrides === 1 ? '' : 's'} in ${cat.name}? They will use ${cat.minLeverage}x / ${cat.defaultLeverage}x / ${cat.maxLeverage}x.`) &&
    void run(async () => {
      const r = await api.post<{ cleared: number }>(`/api/admin/instrument-categories/${cat.id}/clear-overrides`, {});
      setNote(`Cleared the overrides of ${r.cleared} instrument${r.cleared === 1 ? '' : 's'}.`);
      onSaved(cat);
    });
  const remove = () =>
    cat &&
    window.confirm(`Delete the empty category ${cat.name}?`) &&
    void run(async () => {
      await api.del(`/api/admin/instrument-categories/${cat.id}`);
      onDeleted?.();
    });
  const field = (k: 'min' | 'def' | 'max', label: string) => (
    <label className="flex flex-col gap-1 text-[11px] text-muted">
      {label}
      <input value={v[k]} onChange={(e) => setV((x) => ({ ...x, [k]: e.target.value }))} inputMode="numeric" className={cx(input, 'num w-24')} />
    </label>
  );
  return (
    <form onSubmit={save} className="flex flex-col gap-3 rounded-xl border border-line bg-panel p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="m-0 text-sm font-semibold">{cat ? `${cat.name} · category leverage` : 'New category'}</h2>
        {cat && (
          <span className="text-[11.5px] text-muted">
            Applies to {cat.instruments - (overrides ?? 0)} of {cat.instruments} instruments; the rest have their own leverage
          </span>
        )}
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-[11px] text-muted">
          Name
          <input value={v.name} onChange={(e) => setV((x) => ({ ...x, name: e.target.value }))} maxLength={40} className={cx(input, 'w-44')} />
        </label>
        {field('min', 'Min')}
        {field('def', 'Default')}
        {field('max', 'Max')}
        <button type="submit" disabled={busy} className="h-9 rounded-md bg-accent px-4 text-xs font-semibold text-white disabled:opacity-50">
          {busy ? <Spinner /> : cat ? 'Save category' : 'Create category'}
        </button>
        {cat && !!overrides && (
          <button type="button" disabled={busy} onClick={clear} className={cx(btn, 'h-9')}>
            Clear overrides ({overrides})
          </button>
        )}
        {cat && !cat.assetClass && !cat.instruments && (
          <button type="button" disabled={busy} onClick={remove} className={cx(btn, 'h-9 text-down')}>
            Delete category
          </button>
        )}
      </div>
      {!cat && <p className="m-0 text-[11.5px] text-muted">Move instruments into it from their Edit dialog.</p>}
      {note && <p className="m-0 text-xs text-up">{note}</p>}
      <Err msg={err} />
    </form>
  );
}

function InstrumentsTab() {
  const { data, error, reload } = useLoad<AdminInstrument[]>('/api/admin/instruments');
  const cats = useLoad<InstrumentCategory[]>('/api/admin/instrument-categories');
  // 'all', 'new' (create form) or a category id.
  const [view, setView] = useState<string>('all');
  const [edit, setEdit] = useState<AdminInstrument | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<{ inst: AdminInstrument; mode: 'archive' | 'delete' } | null>(null);
  const [term, setTerm] = useState('');
  const restore = async (i: AdminInstrument) => {
    try {
      await api.post(`/api/admin/instruments/${i.id}/restore`);
      setMsg(`${i.symbol} restored. Switch Enabled on to trade it again.`);
      await reload();
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message : 'Failed');
    }
  };
  const reloadAll = async () => {
    await Promise.all([reload(), cats.reload()]);
  };
  const toggle = async (i: Instrument) => {
    try {
      await api.patch(`/api/admin/instruments/${i.id}`, { enabled: !i.enabled });
      await reload();
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message : 'Failed');
    }
  };
  const cat = cats.data?.find((c) => String(c.id) === view);
  // Categories (and the leverage range they set) are managed by admins; a dealer can filter by them.
  const dealer = useMe()?.role === 'dealer';
  const active = data?.filter((i) => !i.archivedAt);
  const archived = data?.filter((i) => i.archivedAt) ?? [];
  const inView = cat ? active?.filter((i) => i.categoryId === cat.id) : view === 'all' ? active : view === 'archived' ? archived : [];
  // The search ranks like the client's market search (symbol, name, codes like "gold" or "nasdaq"), then adds the
  // instruments whose category, price source or provider symbol holds the term, within the chip on screen.
  const q = term.trim().toLowerCase();
  const rows = (() => {
    if (!q || !inView) return inView;
    const hits = searchInstruments(inView, term).map((h) => h.item);
    const seen = new Set(hits.map((i) => i.id));
    const more = inView.filter((i) => !seen.has(i.id) && [i.category, i.priceSource, ...Object.values(i.providerSymbols ?? {})].some((v) => v?.toLowerCase().includes(q)));
    return [...hits, ...more];
  })();
  const chip = (id: string, label: ReactNode) => (
    <button key={id} type="button" aria-pressed={view === id} onClick={() => setView(id)} className={cx('h-8 rounded-full border px-3 text-xs font-semibold', view === id ? 'border-accent bg-accent text-white' : 'border-line text-muted hover:text-fg', id === 'new' && view !== id && 'border-dashed')}>
      {label}
    </button>
  );
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        {chip('all', <>All · {active?.length ?? '…'}</>)}
        {cats.data?.map((c) => chip(String(c.id), `${c.name} · ${c.instruments}`))}
        {archived.length > 0 && chip('archived', `Archived · ${archived.length}`)}
        {!dealer && chip('new', '+ New category')}
        {!dealer && (
          <button type="button" className="ml-auto h-8 rounded-lg bg-accent px-3 text-xs font-semibold text-white" onClick={() => setAdding(true)}>
            + Add instrument
          </button>
        )}
      </div>
      {adding && cats.data && (
        <AddInstrumentModal
          categories={cats.data}
          onClose={() => setAdding(false)}
          onCreated={(sym) => {
            setAdding(false);
            setMsg(`${sym} created. Its prices start streaming within seconds.`);
            void reloadAll();
          }}
        />
      )}
      {removing && <ArchiveModal inst={removing.inst} mode={removing.mode} onClose={() => setRemoving(null)} onDone={() => void reloadAll()} />}
      <Err msg={error ?? cats.error ?? msg} />
      {view === 'new' && (
        <CategoryForm
          onSaved={(c) => {
            void cats.reload();
            setView(String(c.id));
          }}
        />
      )}
      {cat && !dealer && (
        <CategoryForm
          cat={cat}
          overrides={inView?.filter(hasOverride).length ?? 0}
          onSaved={() => void reloadAll()}
          onDeleted={() => {
            setView('all');
            void cats.reload();
          }}
        />
      )}
      {view !== 'new' && (
        <div className="flex flex-wrap items-center gap-3">
          <input type="search" value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Search symbol, name, category or provider symbol" className={cx(input, 'w-80')} aria-label="Search instruments" />
          <span className="text-xs text-muted">
            {q && rows && inView ? `${rows.length} of ${inView.length} instruments` : `${inView?.length ?? 0} instruments`}
          </span>
        </div>
      )}
      {view !== 'new' && (
        <Table head={['Symbol', 'Category', 'Source', 'Enabled', 'Leverage min / default / max', 'Spread', 'Markup', 'Fee', 'Swap Buy / Sell', 'Min / Max ($)', '1 lot', '']} minWidth={1250}>
          {rows?.map((i) => (
            <tr key={i.id} className="num border-b border-line">
              <td className={cx(td, 'font-sans font-semibold')}>
                <span className="flex items-center gap-2">
                  <InstrumentIcon symbol={i.symbol} size={18} />
                  {i.symbol}
                </span>
              </td>
              <td className={cx(td, 'font-sans text-muted')}>{i.category}</td>
              <td className={cx(td, 'font-sans text-muted')} title={Object.entries(i.providerSymbols ?? {}).map(([p, v]) => `${p}: ${v}`).join('\n')}>
                {i.priceSource === 'provider' || i.priceSource === 'binance' ? 'Routing' : i.priceSource}
              </td>
              <td className={td}>
                <label className="flex cursor-pointer items-center gap-1.5 font-sans">
                  <input type="checkbox" checked={i.enabled} onChange={() => void toggle(i)} />
                  {i.enabled ? 'On' : 'Off'}
                </label>
              </td>
              <td className={td}>
                <span className="flex items-center gap-2">
                  {i.minLeverage}x / {i.defaultLeverage}x / {i.maxLeverage}x
                  {hasOverride(i) && <span className="rounded-full border border-accent px-1.5 font-sans text-[10.5px] text-accent">Own</span>}
                </span>
              </td>
              <td className={td}>{i.spreadPips === null ? `${i.spreadBps} bps` : `${i.spreadPips} pip${i.spreadPips === '1' ? '' : 's'} fixed`}</td>
              <td className={td}>{i.spreadPips === null ? `${i.markupBps} bps` : '—'}</td>
              <td className={td}>{openFeeLabel(i.feeRate, i.feeFlat) || '0%'}</td>
              <td className={td}>
                {i.swapLongRate}% / {i.swapShortRate}%
              </td>
              <td className={td}>
                {fmt(i.minNotional, 0)} / {fmt(i.maxNotional, 0)}
              </td>
              <td className={td}>
                {fmt(i.lotSize, 0)} {i.unit}
              </td>
              <td className={cx(td, 'font-sans')}>
                <span className="flex gap-1.5">
                  {i.archivedAt ? (
                    !dealer && (
                      <>
                        <button type="button" className={btn} onClick={() => void restore(i)}>
                          Restore
                        </button>
                        <button type="button" className={cx(btn, 'text-down')} onClick={() => setRemoving({ inst: i, mode: 'delete' })}>
                          Delete
                        </button>
                      </>
                    )
                  ) : (
                    <>
                      <button type="button" className={btn} onClick={() => setEdit(i)}>
                        Edit
                      </button>
                      {!dealer && (
                        <button type="button" className={btn} onClick={() => setRemoving({ inst: i, mode: 'archive' })}>
                          Archive
                        </button>
                      )}
                    </>
                  )}
                </span>
              </td>
            </tr>
          ))}
        </Table>
      )}
      {q && rows && !rows.length && inView?.length ? <Empty>No instrument matches &ldquo;{term.trim()}&rdquo;.</Empty> : null}
      {cat && inView && !inView.length && <Empty>No instruments in {cat.name} yet. Move one here from its Edit dialog.</Empty>}
      <p className="m-0 text-[11.5px] text-muted">
        Clients trade between min and max leverage, and the order panel opens at the default. &quot;Own&quot; marks an instrument with its own leverage instead of its category&apos;s. Spread, markup, depth and enable/disable apply to live prices within a second. Newly enabled instruments start streaming after a feeder restart.
      </p>
      {edit && <InstrumentEditor inst={edit} categories={cats.data ?? []} onClose={() => setEdit(null)} onSaved={() => void reloadAll()} />}
    </div>
  );
}

// ---------- Hedging ----------

interface HedgingReport {
  defaultLevel: string;
  customClients: { userId: string; email: string; name: string | null; externalClientId: string | null; level: string }[];
  rows: {
    symbol: string;
    clients: number;
    clientNetUnits: string;
    targetUnits: string;
    keepUnits: string;
    mark: string | null;
    clientNetNotional: string | null;
    targetNotional: string | null;
    keepNotional: string | null;
  }[];
  totals: { clientNetNotional: string; targetNotional: string; keepNotional: string };
}

function HedgingTab() {
  const { data, error, reload } = useLoad<HedgingReport>('/api/admin/hedging');
  const [showCustom, setShowCustom] = useState(false);
  const [openUser, setOpenUser] = useState<string | null>(null);
  const customIndex = data && openUser ? data.customClients.findIndex((c) => c.userId === openUser) : -1;
  useEffect(() => {
    const t = window.setInterval(() => void reload(), 5000);
    return () => window.clearInterval(t);
  }, [reload]);
  const units = (v: string) => fmt(v, 4);
  return (
    <div className="flex flex-col gap-3">
      <Err msg={error} />
      {data && (
        <div className="flex flex-col gap-3 rounded-xl border border-line bg-panel p-4">
          <div className="flex flex-wrap items-end gap-4">
            <HedgeLevelInput
              label="Hedge level · all clients"
              value={data.defaultLevel}
              onSave={async (level) => {
                await api.put('/api/admin/hedging/settings', { defaultLevel: level });
                await reload();
              }}
            />
            <button type="button" className="pb-2.5 text-xs text-muted underline-offset-2 hover:text-fg hover:underline" onClick={() => setShowCustom((x) => !x)}>
              {data.customClients.length} client{data.customClients.length === 1 ? ' has its' : 's have their'} own level{data.customClients.length ? (showCustom ? ' · hide' : ' · show') : ''}
            </button>
          </div>
          {showCustom && !!data.customClients.length && (
            <ul className="m-0 flex list-none flex-col gap-1 p-0 text-xs">
              {data.customClients.map((c) => (
                <li key={c.userId} className="flex gap-3">
                  <button type="button" className="text-left text-fg hover:underline" title={c.email} onClick={() => setOpenUser(c.userId)}>
                    {clientLabel({ name: c.name, email: c.email, crmId: c.externalClientId })}
                  </button>
                  <span className="num text-muted">{toPct(c.level)}%</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {data && (
        <div className="flex flex-wrap gap-6 text-xs text-muted">
          <span>
            Client net exposure <span className="num text-fg">{fmtUsd(data.totals.clientNetNotional)}</span>
          </span>
          <span>
            Target hedge <span className="num text-fg">{fmtUsd(data.totals.targetNotional)}</span>
          </span>
          <span>
            House keeps <span className="num text-fg">{fmtUsd(data.totals.keepNotional)}</span>
          </span>
        </div>
      )}
      <Table head={['Instrument', 'Clients', 'Client net (units)', 'Target hedge (units)', 'At the LP', 'Target ($)', 'House keeps (units)', 'House keeps ($)', 'Mark']}>
        {data?.rows.map((r) => {
          const t = D.from(r.targetUnits);
          return (
            <tr key={r.symbol} className="num border-b border-line">
              <td className={cx(td, 'font-sans font-semibold')}>
                <span className="flex items-center gap-2">
                  <InstrumentIcon symbol={r.symbol} size={18} />
                  {r.symbol}
                </span>
              </td>
              <td className={td}>{r.clients}</td>
              <td className={cx(td, pnlCls(r.clientNetUnits))}>{units(r.clientNetUnits)}</td>
              <td className={cx(td, 'font-semibold')}>{units(t.abs().toString())}</td>
              <td className={cx(td, 'font-sans font-semibold', t.isPos() ? 'text-up' : t.isNeg() ? 'text-down' : 'text-muted')}>{t.isPos() ? 'Buy' : t.isNeg() ? 'Sell' : '—'}</td>
              <td className={td}>{r.targetNotional ? fmt(D.from(r.targetNotional).abs().toString(), 2) : '—'}</td>
              <td className={td}>{units(r.keepUnits)}</td>
              <td className={td}>{r.keepNotional ? fmt(r.keepNotional, 2) : '—'}</td>
              <td className={td}>{r.mark ? fmt(r.mark, 4) : '—'}</td>
            </tr>
          );
        })}
      </Table>
      {data && !data.rows.length && <Empty>No open live positions</Empty>}
      <p className="m-0 text-[11.5px] text-muted">
        Target hedge = each live client&apos;s net volume × their hedge level, summed per instrument. Buy or sell that amount at the liquidity provider; nothing is placed automatically. House keeps = what stays on the house book (B-book), in client terms: + means clients are net long there. Demo accounts are not counted.
      </p>
      {openUser && (
        <UserDetail
          key={openUser}
          id={openUser}
          onClose={() => setOpenUser(null)}
          onChanged={() => void reload()}
          nav={customIndex >= 0 && data ? { index: customIndex, total: data.customClients.length, go: (i) => setOpenUser(data.customClients[i]?.userId ?? openUser) } : undefined}
        />
      )}
    </div>
  );
}

// ---------- Exposure ----------

interface Exposure {
  kind: string;
  rows: {
    symbol: string;
    positions: number;
    longUnits: string;
    shortUnits: string;
    clientNetUnits: string;
    houseNetUnits: string;
    mark: string | null;
    longNotional: string | null;
    shortNotional: string | null;
    houseNetNotional: string | null;
    clientUnrealizedPnl: string;
    houseUnrealizedPnl: string;
  }[];
  totals: { houseUnrealizedPnl: string; grossNotional: string };
}

function ExposureTab() {
  const [kind, setKind] = useState('live');
  const { data, error, reload } = useLoad<Exposure>(`/api/admin/exposure?kind=${kind}`);
  useEffect(() => {
    const t = window.setInterval(() => void reload(), 5000);
    return () => window.clearInterval(t);
  }, [reload]);
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-4">
        <KindToggle value={kind} onChange={setKind} />
        {data && (
          <>
            <span className="text-xs text-muted">
              Gross notional <span className="num text-fg">{fmtUsd(data.totals.grossNotional)}</span>
            </span>
            <span className="text-xs text-muted">
              House unrealized PnL <span className={cx('num', pnlCls(data.totals.houseUnrealizedPnl))}>{fmtUsdSigned(data.totals.houseUnrealizedPnl)}</span>
            </span>
          </>
        )}
      </div>
      <Err msg={error} />
      <Table head={['Instrument', 'Positions', 'Client buy', 'Client sell', 'House net (units)', 'Mark', 'House net notional ($)', 'Client uPnL ($)', 'House uPnL ($)']}>
        {data?.rows.map((r) => (
          <tr key={r.symbol} className="num border-b border-line">
            <td className={cx(td, 'font-sans font-semibold')}>
              <span className="flex items-center gap-2">
                <InstrumentIcon symbol={r.symbol} size={18} />
                {r.symbol}
              </span>
            </td>
            <td className={td}>{r.positions}</td>
            <td className={cx(td, 'text-up')}>{r.longUnits}</td>
            <td className={cx(td, 'text-down')}>{r.shortUnits}</td>
            <td className={cx(td, pnlCls(r.houseNetUnits), 'font-semibold')}>{r.houseNetUnits}</td>
            <td className={td}>{r.mark ? fmt(r.mark, 4) : '—'}</td>
            <td className={cx(td, pnlCls(r.houseNetNotional))}>{r.houseNetNotional ? fmt(r.houseNetNotional, 2) : '—'}</td>
            <td className={cx(td, pnlCls(r.clientUnrealizedPnl))}>{fmtSigned(r.clientUnrealizedPnl, 2)}</td>
            <td className={cx(td, pnlCls(r.houseUnrealizedPnl), 'font-semibold')}>{fmtSigned(r.houseUnrealizedPnl, 2)}</td>
          </tr>
        ))}
      </Table>
      {data && !data.rows.length && <Empty>No open {kind} positions</Empty>}
      <p className="m-0 text-[11.5px] text-muted">B-book: the house holds the opposite side of every client position. Negative house net = the house is short.</p>
    </div>
  );
}

// ---------- Webhooks ----------

interface Hook {
  id: string;
  event_id: string;
  event: string;
  status: string;
  attempts: number;
  last_error: string | null;
  created_at: string;
  sent_at: string | null;
}

function WebhooksTab() {
  const [status, setStatus] = useState('');
  const { data, error, reload } = useLoad<Hook[]>(`/api/admin/webhooks${status ? `?status=${status}` : ''}`);
  const retry = async (id: string) => {
    await api.post(`/api/admin/webhooks/${id}/retry`).catch(() => {});
    await reload();
  };
  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-2">
        {['', 'pending', 'sent', 'failed'].map((s) => (
          <button key={s || 'all'} type="button" aria-pressed={status === s} onClick={() => setStatus(s)} className={cx(btn, status === s && 'bg-raised')}>
            {s || 'All'}
          </button>
        ))}
      </div>
      <Err msg={error} />
      <Table head={['#', 'Event', 'Status', 'Attempts', 'Created', 'Sent', 'Last error', '']}>
        {data?.map((h) => (
          <tr key={h.id} className="num border-b border-line">
            <td className={cx(td, 'text-muted')}>{h.id}</td>
            <td className={cx(td, 'font-sans')}>{h.event}</td>
            <td className={cx(td, h.status === 'sent' ? 'text-up' : h.status === 'failed' ? 'text-down' : 'text-warn')}>{h.status}</td>
            <td className={td}>{h.attempts}</td>
            <td className={cx(td, 'text-muted')}>{fmtDateTime(h.created_at)}</td>
            <td className={cx(td, 'text-muted')}>{h.sent_at ? fmtDateTime(h.sent_at) : '—'}</td>
            <td className={cx(td, 'max-w-[260px] truncate font-sans text-muted')}>{h.last_error ?? ''}</td>
            <td className={td}>
              {h.status === 'failed' && (
                <button type="button" className={btn} onClick={() => void retry(h.id)}>
                  Retry
                </button>
              )}
            </td>
          </tr>
        ))}
      </Table>
      {data && !data.length && <Empty>No webhook events{status ? ` with status ${status}` : ''}. Set CRM_WEBHOOK_URL to enable delivery.</Empty>}
    </div>
  );
}

// ---------- page ----------

const CRM_TABS: Tab[] = ['kyc', 'sof', 'withdrawals', 'deposits', 'support'];

export function AdminPage() {
  const dealer = useMe()?.role === 'dealer';
  const [picked, setTab] = useState<Tab>(() => {
    const t = new URLSearchParams(window.location.search).get('tab') as Tab;
    return ALL_TABS.includes(t) ? t : 'home';
  });
  const inCrm = useBackofficeInCrm();
  const hidden = dealer ? ALL_TABS.filter((t) => !DEALER_TABS.includes(t)) : inCrm ? CRM_TABS : [];
  const tab: Tab = hidden.includes(picked) ? 'home' : picked;
  const nav = navFor(dealer, hidden);
  const current = itemOf(nav, tab);
  useTeamHeartbeat(TAB_LABEL[tab]);
  useEffect(() => {
    document.title = `Admin · ${BRAND.name}`;
    void api
      .get<{ backofficeInCrm: boolean }>('/api/admin/config')
      .then((c) => adminConfig.set(c))
      .catch(() => {});
  }, []);
  // Admin is light, in the Square style, whatever theme the trading app uses (index.css, :root[data-admin]).
  useEffect(() => {
    const root = document.documentElement;
    root.dataset.admin = '';
    paintChrome('#f7f7f7');
    return () => {
      delete root.dataset.admin;
      paintChrome();
    };
  }, []);
  const go = (t: Tab) => {
    setTab(t);
    window.history.replaceState({}, '', `/admin?tab=${t}`);
    document.getElementById('admin-main')?.scrollTo({ top: 0 });
  };
  const title = current?.label ?? TAB_LABEL[tab];
  const actions: [string, Tab][] =
    tab !== 'home' ? [] : dealer ? [['Hedge now', 'hedging']] : [['Credit / debit', 'accounts'], ['Add instrument', 'instruments'], ['Send notification', 'push']];
  return (
    <div className="flex min-h-full flex-col bg-bg text-fg lg:h-full lg:flex-row">
      <AdminNav nav={nav} tab={tab} onPick={go} title={title} dealer={dealer} />
      <div id="admin-main" className="flex min-w-0 flex-1 flex-col gap-2.5 px-4 pt-2 pb-12 lg:overflow-y-auto lg:px-7 lg:pt-5 [&>*]:shrink-0">
        <header className="hidden flex-wrap items-center justify-between gap-3 pb-4 lg:flex">
          <h1 className="m-0 text-[15px] font-semibold">{title}</h1>
          <div className="flex flex-wrap items-center gap-1">
            {actions.map(([label, t]) => (
              <button key={label} type="button" onClick={() => go(t)} className="h-9 rounded-full px-2.5 text-[15px] font-medium text-muted hover:text-fg">
                {label}
              </button>
            ))}
          </div>
        </header>
        {current && current.tabs.length > 1 && (
          <div role="tablist" aria-label={`${title} sections`} className="flex flex-wrap gap-2 pb-2">
            {current.tabs.map((t) => (
              <button
                key={t}
                type="button"
                role="tab"
                aria-selected={t === tab}
                onClick={() => go(t)}
                className={cx('h-10 rounded-full px-4 text-sm font-medium', t === tab ? 'bg-fg text-bg' : 'bg-[#ebebeb] text-fg hover:bg-[#e2e2e2]')}
              >
                {TAB_LABEL[t]}
              </button>
            ))}
          </div>
        )}
        <SectionBoundary key={tab}>
          {tab === 'home' && (dealer ? <DealerHome go={go} /> : <AdminHome go={go} hidden={hidden} />)}
          {tab === 'accounts' && <DeskAccountsTab />}
          {tab === 'online' && <OnlineTab />}
          {tab === 'risk' && <RiskDeskTab />}
          {tab === 'summary' && <DailySummaryTab />}
          {tab === 'approvals' && <ApprovalsTab />}
          {tab === 'audit' && <AuditTab />}
          {tab === 'staff' && <StaffTab />}
          {tab === 'signin' && <MySignInSecurityTab />}
          {tab === 'guide' && <GuideTab />}
          {tab === 'users' && <UsersTab />}
          {tab === 'kyc' && <KycTab />}
          {tab === 'sof' && <SofTab />}
          {tab === 'rollout' && <RolloutTab />}
          {tab === 'withdrawals' && <WithdrawalsTab />}
          {tab === 'limits' && <WithdrawLimitsTab />}
          {tab === 'verifywallets' && <VerificationWalletsTab />}
          {tab === 'fees' && <FeesTab />}
          {tab === 'deposits' && <DepositsTab />}
          {tab === 'support' && <SupportTab />}
          {tab === 'positions' && <PositionsTab />}
          {tab === 'instruments' && <InstrumentsTab />}
          {tab === 'feeds' && <FeedsTab />}
          {tab === 'providers' && <ProvidersTab />}
          {tab === 'earn' && <EarnSettingsTab />}
          {tab === 'crypto_pro' && <CryptoProTab />}
          {tab === 'yield' && <YieldAdminTab />}
          {tab === 'academy' && <AcademyAdminTab />}
          {tab === 'hedging' && <HedgingTab />}
          {tab === 'exposure' && <ExposureTab />}
          {tab === 'push' && <PushTab />}
          {tab === 'webhooks' && <WebhooksTab />}
          {tab === 'meme' && <MemeTab />}
          {tab === 'features' && <FeaturesTab />}
          {tab === 'copy' && <CopyTab />}
          {tab === 'masters' && <MastersTab />}
          {tab === 'portfolios' && <SmartPortfoliosTab />}
        </SectionBoundary>
      </div>
      <DeskNotices />
    </div>
  );
}
